import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { hrAPI } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { toast } from 'sonner';
import { AlertTriangle, CheckCircle, Clock, ArrowRight } from 'lucide-react';

export default function PendingSkillUpdatesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === 'admin';
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { setRows((await hrAPI.pendingSkillUpdates()).data || []); }
    catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const markDone = async (r) => {
    if (!window.confirm(`Mark skill update done for ${r.employee_name}? (Update their skill level on the profile first.)`)) return;
    try { await hrAPI.markSkillUpdated(r.session_id, r.employee_id); toast.success('Marked as updated'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const extend = async (r) => {
    const d = window.prompt('Extend skill-update due date to (YYYY-MM-DD):', r.due_date || '');
    if (!d) return;
    try { await hrAPI.extendDue(r.session_id, r.employee_id, { new_due_date: d }); toast.success('Due date extended'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="space-y-5" data-testid="hr-pending-updates-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-[#0F172A] flex items-center gap-2"><Clock className="w-6 h-6 text-pink-600" /> Pending Skill Updates</h1>
        <p className="text-sm text-slate-500 mt-0.5">Completed hard-skill trainings awaiting a skill-level update (mandatory within 1 month)</p>
      </div>
      <Card className="border border-slate-200 shadow-none"><CardContent className="p-0">
        <Table>
          <TableHeader><TableRow className="bg-slate-50/80">
            <TableHead className="text-xs">Employee</TableHead><TableHead className="text-xs">Training</TableHead>
            <TableHead className="text-xs">Test Score</TableHead><TableHead className="text-xs">Due</TableHead>
            <TableHead className="text-xs text-right">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {loading ? <TableRow><TableCell colSpan={5} className="text-center text-slate-400 py-8 text-sm">Loading...</TableCell></TableRow> :
              rows.length === 0 ? <TableRow><TableCell colSpan={5} className="text-center text-slate-400 py-8 text-sm"><CheckCircle className="w-5 h-5 mx-auto text-emerald-500 mb-1" />No pending skill updates</TableCell></TableRow> :
                rows.map((r, i) => (
                  <TableRow key={i} className={r.overdue ? 'bg-red-50/40' : ''} data-testid={`hr-pending-row-${r.session_id}-${r.employee_id}`}>
                    <TableCell className="text-sm font-medium">{r.employee_name}</TableCell>
                    <TableCell className="text-sm text-slate-600">{r.training_title}</TableCell>
                    <TableCell className="text-sm">{r.test_score != null ? `${r.test_score}/${r.test_total}` : '—'}</TableCell>
                    <TableCell className="text-xs font-mono">{r.due_date || '—'} {r.overdue && <Badge variant="outline" className="text-[9px] bg-red-50 text-red-700 border-red-200 ml-1"><AlertTriangle className="w-2.5 h-2.5 mr-0.5" />Overdue</Badge>}</TableCell>
                    <TableCell className="text-right space-x-1">
                      <Button size="sm" variant="ghost" className="h-7 text-xs gap-1" onClick={() => navigate(`/hr/employees/${r.employee_id}${(r.skill_ids && r.skill_ids.length) ? `?add_skills=${r.skill_ids.join(',')}` : ''}`)}>Update level <ArrowRight className="w-3 h-3" /></Button>
                      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => markDone(r)} data-testid={`hr-pending-done-${r.session_id}-${r.employee_id}`}>Mark done</Button>
                      {isAdmin && <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => extend(r)} data-testid={`hr-pending-extend-${r.session_id}-${r.employee_id}`}>Extend</Button>}
                    </TableCell>
                  </TableRow>
                ))}
          </TableBody>
        </Table>
      </CardContent></Card>
    </div>
  );
}
